import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChangeComparison, formatDate, OutcomeMetrics, OutcomeVerdict, TargetResults, TargetType } from '../../services/learningApi';
import { HelpTip, useApis } from './common';

const RANGES = [7, 30, 90] as const;
const VERSION_COLORS = ['#1976d2', '#9c27b0', '#2e7d32', '#ed6c02', '#0288d1', '#d32f2f', '#6d4c41', '#546e7a'];

export const pct = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`);
export const score = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(2)}`);
const ms = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v >= 1000 ? `${(v / 1000).toFixed(1)}s` : `${v}ms`);
const shortDate = (d: string) => d.slice(5);

interface Props {
  targetType: TargetType;
  targetId: string;
  refreshKey: number;
}

export const ResultsTab: React.FC<Props> = ({ targetType, targetId, refreshKey }) => {
  const apis = useApis();
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<TargetResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await apis.learning.results(targetType, targetId, days));
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apis, targetType, targetId, days]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (error && !data) return <Alert severity="error">{error}</Alert>;
  if (!data) return <CircularProgress />;

  const t = data.totals;
  const p = data.previous;
  const minRatings = data.thresholds.min_ratings;

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            How this {targetType} is used and how people rate its results, including every run, not just the ones analyzed. Ratings come from
            feedback on runs (thumbs, app outcomes like logged/edited/discarded) and count toward the day and version of the run they’re about.
            Everything here covers the range on the right; how many runs are waiting for the next analysis is on Overview.
          </Typography>
        </Box>
        {loading && <CircularProgress size={18} />}
        <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}>
          {RANGES.map((r) => (
            <ToggleButton key={r} value={r}>
              {r} days
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md>
          <Metric
            label={`Runs (last ${data.days} days)`}
            value={t.runs.toLocaleString()}
            hint={`${(t.runs / data.days).toFixed(t.runs / data.days < 10 ? 1 : 0)} per day`}
            delta={p.runs ? (t.runs - p.runs) / p.runs : null}
            deltaText={(d) => `${d > 0 ? '+' : ''}${Math.round(d * 100)}% vs previous ${data.days} days`}
            neutral
          />
        </Grid>
        <Grid item xs={6} md>
          <Metric
            label="Success rate"
            value={pct(t.success_rate)}
            hint={`${t.failed} failed`}
            delta={t.success_rate !== null && p.success_rate !== null ? t.success_rate - p.success_rate : null}
            deltaText={(d) => `${d > 0 ? '+' : ''}${Math.round(d * 100)} pts vs previous`}
          />
        </Grid>
        <Grid item xs={6} md>
          <Metric
            label="Runs rated"
            value={pct(t.coverage)}
            hint={`${t.rated_runs} runs · ${t.feedback} ratings`}
            help="Share of runs that got any feedback. More coverage makes every other number here more trustworthy."
          />
        </Grid>
        <Grid item xs={6} md>
          <Metric
            label="Positive ratings"
            value={pct(t.pct_positive)}
            hint={t.feedback ? `${t.positive} positive · ${t.negative} negative` : 'No ratings yet'}
            delta={t.pct_positive !== null && p.pct_positive !== null ? t.pct_positive - p.pct_positive : null}
            deltaText={(d) => `${d > 0 ? '+' : ''}${Math.round(d * 100)} pts vs previous`}
            weak={t.feedback > 0 && t.feedback < minRatings}
          />
        </Grid>
        <Grid item xs={6} md>
          <Metric
            label="Average rating"
            value={score(t.avg_score)}
            hint="−1 (bad) to +1 (great)"
            delta={t.avg_score !== null && p.avg_score !== null ? t.avg_score - p.avg_score : null}
            deltaText={(d) => `${d > 0 ? '+' : ''}${d.toFixed(2)} vs previous`}
            help="Each feedback label has a score (e.g. logged +0.8, edited −0.3, discarded −0.8), or the score the sender gave."
            weak={t.feedback > 0 && t.feedback < minRatings}
          />
        </Grid>
      </Grid>

      <UsageChart data={data} targetType={targetType} />
      <FeedbackChart data={data} />

      {targetType === 'function' && data.versions.length > 0 && <VersionsTable data={data} />}
      <ChangesTable changes={data.changes} thresholds={data.thresholds} />
      <KnowledgeComparison data={data} />

      {data.labels.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Feedback labels
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {data.labels.map((l) => (
              <Tooltip key={l.label} title={`Average score ${score(l.avg_score)}`}>
                <Chip size="small" variant="outlined" color={l.avg_score > 0 ? 'success' : l.avg_score < 0 ? 'error' : 'default'} label={`${l.label}: ${l.count}`} />
              </Tooltip>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

const Metric: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: string;
  help?: string;
  delta?: number | null;
  deltaText?: (d: number) => string;
  neutral?: boolean;
  weak?: boolean;
}> = ({ label, value, hint, help, delta, deltaText, neutral, weak }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
      {help && <HelpTip title={help} />}
    </Typography>
    <Typography variant="h5" sx={{ opacity: weak ? 0.6 : 1 }}>
      {value}
    </Typography>
    {hint && (
      <Typography variant="body2" color="text.secondary">
        {hint}
      </Typography>
    )}
    {delta !== null && delta !== undefined && deltaText && Math.abs(delta) >= 0.005 && (
      <Typography variant="caption" sx={{ color: neutral ? 'text.secondary' : delta > 0 ? 'success.main' : 'error.main' }}>
        {deltaText(delta)}
      </Typography>
    )}
    {weak && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Few ratings: early signal
      </Typography>
    )}
  </Paper>
);

type MarkerKind = TargetResults['markers'][number]['kind'];
type Marker = TargetResults['markers'][number] & { tag: string };
type Markers = ReturnType<typeof useMarkers>;
const KIND_ORDER: Record<MarkerKind, number> = { change: 0, version: 1, analysis: 2 };
const KIND_LABEL: Record<MarkerKind, string> = { change: 'Change applied', version: 'New version', analysis: 'Analysis ran' };
const ANALYSIS_COLOR = '#7e57c2';

/** Changes and versions are numbered to match the list under the chart; analyses are tagged "A". One line per day. */
function useMarkers(data: TargetResults) {
  return useMemo(() => {
    let n = 0;
    const items: Marker[] = data.markers.map((m) => ({ ...m, tag: m.kind === 'analysis' ? 'A' : String(++n) }));
    const byDate = new Map<string, Marker[]>();
    items.forEach((m) => byDate.set(m.date, [...(byDate.get(m.date) || []), m]));
    const days = Array.from(byDate, ([date, list]) => ({
      date,
      kind: list.reduce<MarkerKind>((k, m) => (KIND_ORDER[m.kind] < KIND_ORDER[k] ? m.kind : k), list[0].kind),
      text: Array.from(new Set(list.map((m) => m.tag))).join('·'),
    }));
    const lastAnalysis = [...items].reverse().find((m) => m.kind === 'analysis') || null;
    return { items, byDate, days, lastAnalysis, lastDate: data.daily.length ? data.daily[data.daily.length - 1].date : null };
  }, [data.markers, data.daily]);
}

function useMarkerColors(): Record<MarkerKind, string> {
  const theme = useTheme();
  return { change: theme.palette.warning.main, version: theme.palette.info.main, analysis: ANALYSIS_COLOR };
}

const describe = (m: Marker) => (m.kind === 'change' ? `Applied: ${m.label}` : m.label);

const MarkerLabel: React.FC<{ viewBox?: { x: number; y: number }; text: string; color: string }> = ({ viewBox, text, color }) => {
  if (!viewBox) return null;
  const w = 10 + text.length * 6.5;
  return (
    <g>
      <rect x={viewBox.x - w / 2} y={viewBox.y - 16} width={w} height={15} rx={7.5} fill={color} />
      <text x={viewBox.x} y={viewBox.y - 5} textAnchor="middle" fontSize={10} fontWeight={700} fill="#fff">
        {text}
      </text>
    </g>
  );
};

const sinceAnalysisArea = (markers: Markers, yAxisId?: string) =>
  markers.lastAnalysis && markers.lastDate ? (
    <ReferenceArea
      x1={markers.lastAnalysis.date}
      x2={markers.lastDate}
      fill={ANALYSIS_COLOR}
      fillOpacity={0.07}
      {...(yAxisId ? { yAxisId } : {})}
    />
  ) : null;

const markerLines = (markers: Markers, colors: Record<MarkerKind, string>, yAxisId?: string) =>
  markers.days.map((d) => (
    <ReferenceLine
      key={d.date}
      x={d.date}
      stroke={colors[d.kind]}
      strokeWidth={d.kind === 'analysis' ? 1.5 : 2}
      strokeDasharray={d.kind === 'analysis' ? '2 3' : undefined}
      label={<MarkerLabel text={d.text} color={colors[d.kind]} />}
      {...(yAxisId ? { yAxisId } : {})}
    />
  ));

const Tag: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
  <Box
    component="span"
    sx={{ display: 'inline-block', minWidth: 18, px: 0.6, mr: 0.75, borderRadius: 8, bgcolor: color, color: '#fff', fontSize: 10, fontWeight: 700, textAlign: 'center', lineHeight: '15px' }}
  >
    {children}
  </Box>
);

const DayTooltip: React.FC<any> = ({ active, payload, label, markers, colors }) => {
  if (!active || !payload?.length) return null;
  const events: Marker[] = markers.byDate.get(label) || [];
  return (
    <Paper elevation={3} sx={{ p: 1.25, maxWidth: 340 }}>
      <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      {payload.map((p: any) => (
        <Typography key={p.dataKey} variant="caption" sx={{ display: 'block', color: p.color }}>
          {p.name}: {p.value ?? '—'}
          {p.dataKey === 'pct' && p.value != null ? '%' : ''}
        </Typography>
      ))}
      {events.length > 0 && (
        <Box sx={{ mt: 0.75, pt: 0.75, borderTop: 1, borderColor: 'divider' }}>
          {events.map((m) => (
            <Typography key={`${m.at}-${m.tag}`} variant="caption" sx={{ display: 'block', mb: 0.25 }}>
              <Tag color={colors[m.kind]}>{m.tag}</Tag>
              {describe(m)}
            </Typography>
          ))}
        </Box>
      )}
    </Paper>
  );
};

const MarkerLegend: React.FC<{ markers: Markers; colors: Record<MarkerKind, string>; keyOnly?: boolean }> = ({ markers, colors, keyOnly }) => {
  const [showAll, setShowAll] = useState(false);
  if (!markers.items.length) return null;
  const kinds = (Object.keys(KIND_ORDER) as MarkerKind[]).filter((k) => markers.items.some((m) => m.kind === k));
  const analyses = markers.items.filter((m) => m.kind === 'analysis');
  const hidden = new Set(!showAll && analyses.length > 6 ? analyses.slice(0, analyses.length - 3) : []);
  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" spacing={2} sx={{ mb: keyOnly ? 0 : 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
        {kinds.map((k) => (
          <Stack key={k} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 14, height: 0, borderTop: `2px ${k === 'analysis' ? 'dotted' : 'solid'} ${colors[k]}` }} />
            <Typography variant="caption" color="text.secondary">
              {KIND_LABEL[k]}
            </Typography>
          </Stack>
        ))}
        {markers.lastAnalysis && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 14, height: 10, bgcolor: ANALYSIS_COLOR, opacity: 0.18 }} />
            <Typography variant="caption" color="text.secondary">
              Since the last analysis (not analyzed yet)
            </Typography>
          </Stack>
        )}
      </Stack>
      {!keyOnly && (
        <Stack spacing={0.25}>
          {markers.items
            .filter((m) => !hidden.has(m))
            .map((m) => (
              <Typography key={`${m.at}-${m.tag}`} variant="caption" color="text.secondary">
                <Tag color={colors[m.kind]}>{m.tag}</Tag>
                {shortDate(m.date)} · {describe(m)}
              </Typography>
            ))}
          {hidden.size > 0 && (
            <Link component="button" variant="caption" onClick={() => setShowAll(true)} sx={{ alignSelf: 'flex-start' }}>
              Show {hidden.size} earlier analyses
            </Link>
          )}
        </Stack>
      )}
    </Box>
  );
};

const UsageChart: React.FC<{ data: TargetResults; targetType: TargetType }> = ({ data, targetType }) => {
  const theme = useTheme();
  const markers = useMarkers(data);
  const colors = useMarkerColors();
  const versions = useMemo(
    () => Array.from(new Set(data.daily.flatMap((d) => Object.keys(d.by_version || {})))).sort((a, b) => Number(a) - Number(b)),
    [data.daily],
  );
  const canSplit = targetType === 'function' && versions.length > 1;
  const [colorBy, setColorBy] = useState<'outcome' | 'version'>('outcome');
  const mode = canSplit ? colorBy : 'outcome';
  const rows = useMemo(
    () =>
      data.daily.map((d) => ({
        ...d,
        ok: d.runs - d.failed,
        ...Object.fromEntries(versions.map((v) => [`v${v}`, d.by_version?.[v] || 0])),
      })),
    [data.daily, versions],
  );

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
          Runs per day
          <HelpTip title="Orange lines are changes you applied, blue lines new function versions, purple dotted lines analyses (training). Numbers match the list below the chart; hover a day to see what happened. The shaded area is everything since the last analysis." />
        </Typography>
        {canSplit && (
          <ToggleButtonGroup size="small" exclusive value={colorBy} onChange={(_, v) => v && setColorBy(v)}>
            <ToggleButton value="outcome">By outcome</ToggleButton>
            <ToggleButton value="version">By version</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>
      {data.totals.runs === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No runs in this period.
        </Typography>
      ) : (
        <Box sx={{ height: 240 }}>
          <ResponsiveContainer>
            <ComposedChart data={rows} margin={{ top: 22, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
              <XAxis dataKey="date" tickFormatter={shortDate} fontSize={11} minTickGap={16} />
              <YAxis allowDecimals={false} fontSize={11} />
              {sinceAnalysisArea(markers)}
              <ChartTooltip content={<DayTooltip markers={markers} colors={colors} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {mode === 'outcome' ? (
                <>
                  <Bar dataKey="ok" name="Succeeded" stackId="r" fill={theme.palette.primary.main} />
                  <Bar dataKey="failed" name="Failed" stackId="r" fill={theme.palette.error.light} />
                  <Line dataKey="with_learned" name="Got learned knowledge" stroke={theme.palette.secondary.main} dot={false} strokeWidth={2} />
                </>
              ) : (
                versions.map((v, i) => <Bar key={v} dataKey={`v${v}`} name={`v${v}`} stackId="r" fill={VERSION_COLORS[i % VERSION_COLORS.length]} />)
              )}
              {markerLines(markers, colors)}
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}
      <MarkerLegend markers={markers} colors={colors} />
    </Paper>
  );
};

const FeedbackChart: React.FC<{ data: TargetResults }> = ({ data }) => {
  const theme = useTheme();
  const markers = useMarkers(data);
  const colors = useMarkerColors();
  const rows = useMemo(() => data.daily.map((d) => ({ ...d, pct: d.pct_positive === null ? null : Math.round(d.pct_positive * 100) })), [data.daily]);

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Ratings per day
        <HelpTip title="Bars: how many ratings the runs from that day received. Line: share of them that were positive. Days with only a few ratings swing a lot." />
      </Typography>
      {data.totals.feedback === 0 ? (
        <Alert severity="info" variant="outlined">
          No feedback on runs in this period. Without it, results are judged by failures, reruns and the analyzer’s own quality check. Feedback
          arrives from the thumbs on run pages, from apps via <code>POST /api/v2/feedback</code>, and from the Passio platform’s request feedback
          for runs it made.
        </Alert>
      ) : (
        <Box sx={{ height: 240 }}>
          <ResponsiveContainer>
            <ComposedChart data={rows} margin={{ top: 22, right: 0, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
              <XAxis dataKey="date" tickFormatter={shortDate} fontSize={11} minTickGap={16} />
              <YAxis yAxisId="n" allowDecimals={false} fontSize={11} />
              <YAxis yAxisId="p" orientation="right" domain={[0, 100]} unit="%" fontSize={11} />
              {sinceAnalysisArea(markers, 'n')}
              <ChartTooltip content={<DayTooltip markers={markers} colors={colors} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="n" dataKey="positive" name="Positive" stackId="f" fill={theme.palette.success.main} />
              <Bar yAxisId="n" dataKey="neutral" name="Neutral" stackId="f" fill={theme.palette.grey[400]} />
              <Bar yAxisId="n" dataKey="negative" name="Negative" stackId="f" fill={theme.palette.error.main} />
              <Line yAxisId="p" dataKey="pct" name="% positive" stroke={theme.palette.text.primary} dot={{ r: 2 }} connectNulls strokeWidth={2} />
              {markerLines(markers, colors, 'n')}
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}
      {data.totals.feedback > 0 && <MarkerLegend markers={markers} colors={colors} keyOnly />}
    </Paper>
  );
};

const Weak: React.FC<{ m: OutcomeMetrics; min: number; children: React.ReactNode }> = ({ m, min, children }) =>
  m.feedback > 0 && m.feedback < min ? (
    <Tooltip title={`Only ${m.feedback} rating${m.feedback === 1 ? '' : 's'}: early signal`}>
      <Box component="span" sx={{ opacity: 0.55 }}>
        {children}
      </Box>
    </Tooltip>
  ) : (
    <>{children}</>
  );

const VersionsTable: React.FC<{ data: TargetResults }> = ({ data }) => {
  const min = data.thresholds.min_ratings;
  const rated = data.versions.filter((v) => v.feedback >= min && v.pct_positive !== null);
  const best = rated.length > 1 ? rated.reduce((a, b) => ((b.pct_positive ?? 0) > (a.pct_positive ?? 0) ? b : a)) : null;
  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        By version
        <HelpTip title="Each version’s runs and ratings in this period. Runs per day counts only the days the version actually ran, so a newer version isn’t penalized for being new." />
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Version</TableCell>
              <TableCell>Ran</TableCell>
              <TableCell align="right">Runs</TableCell>
              <TableCell align="right">Per day</TableCell>
              <TableCell align="right">Success</TableCell>
              <TableCell align="right">Avg time</TableCell>
              <TableCell align="right">Rated</TableCell>
              <TableCell align="right">Positive</TableCell>
              <TableCell align="right">Avg rating</TableCell>
              <TableCell align="right">Got learned knowledge</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.versions.map((v, i) => {
              const activeDays = v.first_day && v.last_day ? Math.max(1, Math.round((Date.parse(v.last_day) - Date.parse(v.first_day)) / 86400000) + 1) : 0;
              return (
                <TableRow key={v.version} sx={{ bgcolor: i % 2 ? 'action.hover' : undefined }}>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography variant="body2" fontWeight={600}>
                        v{v.version}
                      </Typography>
                      {v.is_current && <Chip size="small" color="primary" label="current" />}
                      {best?.version === v.version && (
                        <Tooltip title="Highest share of positive ratings among versions with enough ratings">
                          <StarIcon fontSize="small" color="warning" />
                        </Tooltip>
                      )}
                      {v.from_suggestion && (
                        <Tooltip title={`Created by applying: ${v.from_suggestion.title}`}>
                          <Chip size="small" variant="outlined" label="self-improved" />
                        </Tooltip>
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      created {formatDate(v.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {v.first_day ? `${shortDate(v.first_day)} – ${shortDate(v.last_day || v.first_day)}` : 'No runs'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{v.runs.toLocaleString()}</TableCell>
                  <TableCell align="right">{activeDays ? (v.runs / activeDays).toFixed(1) : '—'}</TableCell>
                  <TableCell align="right">{pct(v.success_rate)}</TableCell>
                  <TableCell align="right">{ms(v.avg_ms)}</TableCell>
                  <TableCell align="right">{v.rated_runs ? `${v.rated_runs} (${pct(v.coverage)})` : '—'}</TableCell>
                  <TableCell align="right">
                    <Weak m={v} min={min}>
                      {pct(v.pct_positive)}
                    </Weak>
                  </TableCell>
                  <TableCell align="right">
                    <Weak m={v} min={min}>
                      {score(v.avg_score)}
                    </Weak>
                  </TableCell>
                  <TableCell align="right">{v.runs ? pct(v.with_learned / v.runs) : '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export const VerdictChip: React.FC<{ verdict: OutcomeVerdict; minRatings: number; minRuns: number }> = ({ verdict, minRatings, minRuns }) => {
  const basis = verdict.basis === 'ratings' ? 'ratings' : 'success rate';
  const detail =
    verdict.basis === 'ratings'
      ? `Positive ratings ${verdict.delta_pct_positive! >= 0 ? '+' : ''}${Math.round((verdict.delta_pct_positive || 0) * 100)} pts, average rating ${score(verdict.delta_avg_score ?? null)}.`
      : verdict.basis === 'success_rate'
        ? `Success rate ${verdict.delta_success_rate! >= 0 ? '+' : ''}${Math.round((verdict.delta_success_rate || 0) * 100)} pts. Not enough ratings to compare those.`
        : '';
  const map = {
    better: { label: 'Better', color: 'success' as const, text: `Results improved after this change (by ${basis}). ${detail}` },
    worse: { label: 'Worse', color: 'error' as const, text: `Results got worse after this change (by ${basis}). ${detail}` },
    same: { label: 'No clear change', color: 'default' as const, text: `No meaningful difference yet (by ${basis}). ${detail}` },
    unknown: {
      label: 'Not enough data',
      color: 'default' as const,
      text: `Needs ${minRatings}+ ratings or ${minRuns}+ runs on each side of the change to compare.`,
    },
  }[verdict.direction];
  return (
    <Tooltip title={map.text}>
      <Chip size="small" color={map.color} variant={verdict.direction === 'unknown' ? 'outlined' : 'filled'} label={map.label} />
    </Tooltip>
  );
};

const Arrow: React.FC<{ before: string; after: string }> = ({ before, after }) => (
  <Typography variant="body2" noWrap>
    <Box component="span" sx={{ color: 'text.secondary' }}>
      {before}
    </Box>{' '}
    → <b>{after}</b>
  </Typography>
);

const ChangesTable: React.FC<{ changes: ChangeComparison[]; thresholds: TargetResults['thresholds'] }> = ({ changes, thresholds }) => (
  <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
      Before and after each change
      <HelpTip
        title={`Compares the same length of time on each side of the change (up to ${thresholds.compare_days} days), so busy and quiet weeks don’t skew it. Changes marked as done by hand are listed too. Other things can change at the same time, so treat it as a strong hint, not proof.`}
      />
    </Typography>
    {changes.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        No suggestions applied yet. When you apply one, this shows how results moved afterwards.
      </Typography>
    ) : (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Change</TableCell>
              <TableCell>Runs</TableCell>
              <TableCell>Success</TableCell>
              <TableCell>Positive ratings</TableCell>
              <TableCell>Avg rating</TableCell>
              <TableCell>Result</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {changes.map((c, i) => (
              <TableRow key={c.id} sx={{ bgcolor: i % 2 ? 'action.hover' : undefined }}>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {c.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(c.decided_at)}
                    {c.new_version ? ` · v${c.new_version}` : ''}
                    {c.manual ? ' · applied by hand' : ''}
                    {c.before ? ` · ${c.window_days} days each side` : ''}
                  </Typography>
                </TableCell>
                {c.before && c.after ? (
                  <>
                    <TableCell>
                      <Arrow before={String(c.before.runs)} after={String(c.after.runs)} />
                    </TableCell>
                    <TableCell>
                      <Arrow before={pct(c.before.success_rate)} after={pct(c.after.success_rate)} />
                    </TableCell>
                    <TableCell>
                      <Arrow before={pct(c.before.pct_positive)} after={pct(c.after.pct_positive)} />
                      <Typography variant="caption" color="text.secondary">
                        {c.before.feedback} → {c.after.feedback} ratings
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Arrow before={score(c.before.avg_score)} after={score(c.after.avg_score)} />
                    </TableCell>
                  </>
                ) : (
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      Applied less than an hour ago.
                    </Typography>
                  </TableCell>
                )}
                <TableCell>
                  <VerdictChip verdict={c.verdict} minRatings={thresholds.min_ratings} minRuns={thresholds.min_runs} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Paper>
);

const KnowledgeComparison: React.FC<{ data: TargetResults }> = ({ data }) => {
  const { with: w, without: wo, published } = data.knowledge;
  const min = data.thresholds.min_ratings;
  const rows: { label: string; get: (m: OutcomeMetrics) => React.ReactNode }[] = [
    { label: 'Runs', get: (m) => m.runs.toLocaleString() },
    { label: 'Success rate', get: (m) => pct(m.success_rate) },
    { label: 'Ratings', get: (m) => m.feedback.toLocaleString() },
    { label: 'Positive ratings', get: (m) => <Weak m={m} min={min}>{pct(m.pct_positive)}</Weak> },
    { label: 'Average rating', get: (m) => <Weak m={m} min={min}>{score(m.avg_score)}</Weak> },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Is learned knowledge helping?
      </Typography>
      {w.runs === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {published
            ? 'No run in this period received learned knowledge. Runs only get it when an in-use entry matches their input.'
            : 'Nothing has been published yet, so no run has received learned knowledge.'}
        </Typography>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Runs that received at least one learned snippet, next to runs that didn’t. The two groups have different inputs, since only matching inputs
            get knowledge, so this is a hint rather than a controlled test. A clearly lower rating with knowledge is worth a look on the Knowledge tab.
          </Typography>
          <TableContainer>
            <Table size="small" sx={{ maxWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell align="right">Got learned knowledge</TableCell>
                  <TableCell align="right">Didn’t</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={r.label} sx={{ bgcolor: i % 2 ? 'action.hover' : undefined }}>
                    <TableCell>{r.label}</TableCell>
                    <TableCell align="right">{r.get(w)}</TableCell>
                    <TableCell align="right">{r.get(wo)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  );
};
